<script>
import NotificationService from '@/services/header/NotificationService'
import { displayToast, formatDate, handleResponseError } from '@/components/utils/globalFunction'

export default {
  name: 'NotificationWidget',
  data() {
    return {
      loading: false,

      page: 1,
      maxPages: null,
      limit: 4,

      notificationList: []
    }
  },
  async created() {
    await this.getNotificationList()
  },
  methods: {
    formatDate,
    async getNotificationList() {
      this.loading = true
      try {
        const responseNotificationListDto = await NotificationService.getNotificationList(
          this.page,
          this.limit
        )
        this.notificationList.push(...responseNotificationListDto.data)
        this.maxPages = responseNotificationListDto.max_page
        this.page = responseNotificationListDto.page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async loadMoreNotification() {
      this.page += 1
      await this.getNotificationList()
    },
    async updateNotificationToOpen(notificationTechnicalId, notificationOpenStatus) {
      if (notificationOpenStatus === false) {
        try {
          const responseNotificationUpdated = await NotificationService.updateNotificationToOpen(
            notificationTechnicalId
          )
          let notificationFound = this.notificationList.find(
            (notification) => notification.technical_id === notificationTechnicalId
          )
          notificationFound.open = responseNotificationUpdated.open
          this.$emit('refreshCount')
          return true
        } catch (error) {
          handleResponseError(error)
          return false
        }
      }
    },
    async updateNotificationToHidden(notificationTechnicalId) {
      try {
        const response = await NotificationService.updateNotificationToHidden(
          notificationTechnicalId
        )

        if (response.technical_id === notificationTechnicalId) {
          this.page = 1
          this.notificationList = []
          await this.getNotificationList()
          this.$emit('refreshCount')
        }
      } catch (error) {
        handleResponseError(error)
      }
    },
    async goToRedirect(notificationObject) {
      let openIsOk = false
      if (notificationObject.open === false) {
        openIsOk = await this.updateNotificationToOpen(
          notificationObject.technical_id,
          notificationObject.open
        )
      } else {
        openIsOk = true
      }

      if (openIsOk) {
        const regex = /^\/.*/
        if (regex.test(notificationObject.redirection_url)) {
          await this.$router.push(notificationObject.redirection_url)
          // force reload page when you navigate in notification repair_file link
          window.location.reload()
        } else {
          window.open(notificationObject.redirection_url, '_blank')
        }
        this.$emit('refreshCount')
      } else {
        displayToast(this.$t('ERROR_STATUS_CODE_MESSAGE.NOT_REDIRECT'), 'error')
      }
    }
  }
}
</script>

<template>
  <div class="notification-container rounded-lg">
    <div class="notification-list">
      <!--Notification loader-->
      <v-list
        v-if="loading"
        class="notification-container-list py-0 cursor-default"
        rounded
        width="300"
      >
        <v-list-item class="pa-0">
          <p class="text-md-center">
            {{ $t('NOTIFICATION.LOADING_NOTIFICATION') }}
          </p>
          <v-progress-linear class="my-5" color="#165039" indeterminate />
        </v-list-item>
      </v-list>

      <!--No Notification to show-->
      <v-list
        v-if="!loading && notificationList.length === 0"
        class="notification-container-list py-0 cursor-default"
        rounded
        width="300"
      >
        <v-list-item class="pa-0">
          <p class="text-md-center">
            {{ $t('NOTIFICATION.NO_NOTIFICATION') }}
          </p>
        </v-list-item>
      </v-list>

      <!--Notification List-->
      <v-list
        v-if="!loading && notificationList.length > 0"
        class="notification-container-list py-0"
        rounded
        width="300"
      >
        <!-- Notification Item -->
        <v-list-item
          v-for="(notification, index) of notificationList"
          :key="notification.technical_id"
          class="pa-0"
        >
          <v-divider v-if="index !== 0" color="white" thickness="2" />

          <div
            class="notification-row"
            :class="[notification.open === false ? 'not-read' : 'read']"
          >
            <!--Title - X -->
            <div class="title-row">
              <!--Title-->
              <h1
                class="title"
                @click="updateNotificationToOpen(notification.technical_id, notification.open)"
              >
                {{ notification.title }}
              </h1>
              <!--X-->
              <button type="button" @click="updateNotificationToHidden(notification.technical_id)">
                <v-icon icon="mdi-close" />
              </button>
            </div>
            <!--Subtitle-->
            <div
              v-if="notification.subtitle"
              class="subtitle-row"
              @click="updateNotificationToOpen(notification.technical_id, notification.open)"
            >
              <h2 class="subtitle">
                {{ notification.subtitle }}
              </h2>
            </div>
            <!--Content-->
            <div
              class="content-row"
              @click="updateNotificationToOpen(notification.technical_id, notification.open)"
            >
              <div class="content">
                {{ notification.content }}
              </div>
            </div>
            <!--Redirect-->
            <div v-if="notification.with_redirection" class="redirect-row">
              <div class="redirect-title" @click="goToRedirect(notification)">
                {{ notification.redirection_message }}
              </div>
            </div>
            <!--Date of creation-->
            <div
              class="date-row"
              @click="updateNotificationToOpen(notification.technical_id, notification.open)"
            >
              {{ formatDate(notification.created_at) }}
            </div>
          </div>
        </v-list-item>
      </v-list>
    </div>

    <!--Show more button-->
    <div v-if="page < maxPages">
      <button @click="loadMoreNotification()" class="btn-show-more-container mx-auto">
        <span class="btn-show-more-text">
          {{ $t('NOTIFICATION.BUTTON.MORE') }}
        </span>
        <v-icon color="#9b9999" icon="mdi-chevron-down" class="btn-show-more-icon" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.notification-container {
  max-height: 450px;
  background: #efefef;
  border: 1px solid #efefef;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: space-between;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.notification-container::-webkit-scrollbar {
  display: none;
}

.btn-show-more-container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.btn-show-more-text {
  color: #9b9999;
}

.btn-show-more-icon {
  margin-top: -10px;
}

.notification-container-list {
  .not-read {
    background-color: #e2f0d9;
  }

  .read {
    background-color: #e3e3e3;
  }

  .notification-row {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .title-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
      align-content: center;
      align-items: center;

      .title {
        font-weight: bold;
        font-size: 16px;
        word-wrap: break-word;
        max-width: 228px;
        line-height: normal;
      }
    }

    .subtitle-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-content: center;
      justify-content: flex-start;
      align-items: center;

      .subtitle {
        font-weight: bold;
        word-wrap: break-word;
        max-width: 255px;
        line-height: normal;
        font-size: small;
      }
    }

    .content-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-content: center;
      justify-content: flex-start;
      align-items: center;
      font-size: small;

      .content {
        word-wrap: break-word;
        max-width: 255px;
        line-height: normal;
      }
    }

    .redirect-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-content: center;
      justify-content: flex-start;
      align-items: center;

      .redirect-title {
        font-size: small;
        cursor: pointer;
        color: #ed7d31;
        word-wrap: break-word;
        max-width: 255px;
      }
    }

    .date-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-content: center;
      justify-content: flex-start;
      align-items: center;
      font-size: x-small;
    }
  }
}
</style>
